/**
 * Пять наборов строк трека и разрешение локали из браузера.
 *
 * Перечень ключей закрытый и объявляется здесь: набор строк, разложенный по
 * пяти файлам без единого объявления, роняет только тот язык, до которого
 * дошли руки. Объявление роняет типы на первом же ключе, которого в нём нет.
 *
 * ПЕРЕВОРОТ блока `WEB-1.1` (спецификация §7, решение `D122`). Было:
 * «переключателя языка в блоке нет вовсе — решение `D29`; выбору нужны профиль
 * и место, где выбор хранится, а в блоке нет ни того, ни другого». Оба условия
 * выполнены: профиль появился (`PATCH /v1/me`), место выбора названо. Поэтому
 * способ сменить язык здесь есть — и он **ровно один**: второе такое имя
 * означало бы, что язык живёт в двух местах и расходится молча.
 *
 * Хранилища выбор не получает (решение `D28`): язык гостя живёт время вкладки,
 * перезагрузка возвращает язык браузера. Порядок разрешения — по порядку
 * браузера (решение `D37`): порядок языков браузера это предпочтение
 * пользователя, и переставлять его клиентом нечем.
 *
 * ПЕРЕВОРОТ блока `WEB-3.2` (спецификация §6). Было: «подписей статусов работы
 * здесь нет намеренно: соответствие „статус → подпись“ — это перечисление в
 * клиенте». Стало: подписи статуса работы есть — ключами, собранными строителем
 * из значения сервера, а не рукописной таблицей (долг `TD-73`); набор ключей
 * сверяется с перечнем контракта, а значение вне его — «неизвестно» (CR-007).
 * Машинное слово статуса до человека на странице отслеживания не доходит.
 *
 * Ключа с именем языка здесь тоже нет, и это отдельное решение. Он существовал
 * ради утверждения «наборы действительно разные», но не доходил до экрана ни на
 * одном языке: строка, переведённая пять раз и не показанная ни разу, — мёртвые
 * данные (AGENTS.md §2 правило 9). Показать её значило бы завести элемент
 * интерфейса, которого не просит ни один сценарий блока (правило 1). Различие
 * наборов проверяется сравнением наборов целиком — оно и сильнее.
 */

import type { User } from '../api/contract';

import { en } from './en';
import { ru } from './ru';
import { tg } from './tg';
import { tr } from './tr';
import { uz } from './uz';

/**
 * Язык интерфейса — тот же перечень, что и в контракте.
 *
 * Рукописная копия перечня расходится с контрактом молча, поэтому перечень
 * берётся из формы профиля, а не переписывается сюда.
 */
export type Locale = User['locale'];

export const SUPPORTED_LOCALES: readonly Locale[] = ['uz', 'ru', 'en', 'tg', 'tr'];

/** Язык, на который приходит браузер, попросивший что-то за пределами пяти. */
export const FALLBACK_LOCALE: Locale = 'ru';

/**
 * Закрытый перечень ключей блока.
 *
 * Ключ, которого здесь нет, не соберётся ни в одном из пяти наборов: набор
 * объявлен этим типом, а не привычкой.
 */
export interface Strings {
  readonly login_title: string;
  readonly login_phone_label: string;
  /**
   * Подпись отправки первой стадии.
   *
   * ПЕРЕВОРОТ блока `WEB-1.1`: ключ назывался `login_phone_submit` и лгал бы на
   * почтовой ветке — подпись служит обоим каналам, а телефон называет только
   * один из них.
   */
  readonly login_code_request: string;
  readonly login_code_label: string;
  readonly login_code_submit: string;
  readonly login_failed: string;
  readonly works_title: string;
  readonly works_more: string;
  readonly works_failed: string;

  // Ключи блока `WEB-1.1` — словарь имён §10. Перечень остаётся закрытым:
  // ключ, положенный в набор и не объявленный здесь, не соберётся, а ключ,
  // объявленный здесь и не дошедший до экрана, красит сторожа сирот.
  readonly login_channel_phone: string;
  readonly login_channel_email: string;
  readonly login_email_label: string;
  readonly login_terms_label: string;
  readonly company_switcher_label: string;
  readonly language_label: string;
  readonly state_loading: string;
  readonly state_empty: string;
  readonly state_error: string;
  readonly state_no_access: string;
  readonly no_membership_title: string;
  readonly no_membership_explain: string;
  readonly company_create_submit: string;
  readonly company_name_label: string;
  /**
   * Подпись поля «тип фирмы» на форме создания фирмы.
   *
   * ПЕРЕВОРОТ блока `WEB-2.1` (ответ ведущего `В2`). Было: `company_type_label`.
   * Стало: `company_form_type_label`. Префикс `company_type_` с этого блока —
   * префикс подписей **значений** перечня типа фирмы, и набор ключей под ним
   * сверяется с перечнем контракта: значения `label` в нём нет, и прежнее имя
   * читалось бы подписью несуществующего типа.
   */
  readonly company_form_type_label: string;
  readonly company_tax_id_label: string;
  readonly company_legal_address_label: string;
  readonly company_members_title: string;
  readonly not_found_title: string;

  // Ключи блока `WEB-1.2` — словарь имён §9. Подписи кабинета директора и
  // профиля пользователя.
  readonly profile_title: string;
  readonly profile_sessions_title: string;
  readonly profile_session_current: string;
  readonly profile_session_never_used: string;
  readonly profile_session_created: string;
  readonly profile_session_last_used: string;
  readonly profile_session_terminate: string;
  readonly profile_channel_add: string;
  readonly profile_channel_code_request: string;
  readonly profile_channel_confirm: string;
  readonly members_title: string;
  readonly members_invite: string;
  readonly invite_channel_phone: string;
  readonly invite_channel_email: string;
  readonly invite_role_label: string;
  readonly invite_submit: string;
  readonly invitations_title: string;
  readonly invitation_revoke: string;
  readonly invitation_resend: string;
  readonly rights_title: string;
  readonly rights_grants: string;
  readonly rights_denies: string;
  readonly rights_grant_add: string;
  readonly rights_deny_add: string;
  readonly rights_remove: string;
  readonly company_profile_title: string;
  readonly company_documents_title: string;
  // Подписи типа фирмы. ПЕРЕВОРОТ блока `WEB-2.1` (долг `TD-73`). Было:
  // ключи названы литералами, строителя нет, подпись собирала рукописная
  // таблица. Стало: ключи собирает строитель `companyTypeStringKey` по
  // префиксу, перечень `COMPANY_TYPES` порождается из контракта, и набор
  // ключей префикса сверяется с ним. Значение вне перечня — «неизвестно» без
  // исходной строки (CR-007).
  readonly company_type_transport: string;
  readonly company_type_logistics: string;
  readonly member_documents_title: string;
  readonly document_download: string;
  readonly enum_unknown: string;

  // Ключи блока `WEB-1.3` — словарь имён §9. Транспорт фирмы, цикл ремонта,
  // сервисная история и заявки на освобождение VIN.
  readonly fleet_title: string;
  readonly fleet_add: string;
  readonly fleet_added: string;
  readonly fleet_add_already: string;
  readonly fleet_vin: string;
  readonly fleet_tech_passport: string;
  readonly fleet_kind_label: string;
  readonly fleet_body_type_label: string;
  readonly fleet_withdraw: string;
  readonly fleet_withdrawn_at: string;
  readonly fleet_empty: string;
  readonly service_title: string;
  readonly service_state_label: string;
  readonly service_state_change: string;
  readonly service_records_title: string;
  readonly service_record_add: string;
  readonly service_description: string;
  readonly service_parts: string;
  readonly service_cost: string;
  readonly service_currency: string;
  readonly service_records_empty: string;
  readonly vin_claims_title: string;
  readonly vin_claim_create: string;
  readonly vin_claim_object: string;
  readonly vin_claim_window_ends_at: string;
  readonly vin_claims_empty: string;

  // Подписи значений перечней контракта. Ключи собираются строителями из
  // значения, пришедшего с сервера, и потому литералом на экране не
  // встречаются ни разу; набор ключей префикса сверяется с перечнем контракта.
  readonly role_director: string;
  readonly role_logist: string;
  readonly role_dispatcher: string;
  readonly role_accountant: string;
  readonly role_mechanic: string;
  readonly role_driver: string;
  readonly member_status_active: string;
  readonly member_status_closed: string;
  readonly permission_company_staff_manage: string;
  readonly permission_company_rights_manage: string;
  readonly permission_company_fleet_manage: string;
  readonly permission_company_fleet_repair: string;
  readonly permission_company_docs_manage: string;
  readonly permission_company_listings_manage: string;
  readonly permission_company_cargo_take: string;
  readonly permission_company_works_assign: string;
  readonly permission_company_accounting_manage: string;
  readonly document_status_pending: string;
  readonly document_status_verified: string;
  readonly document_status_rejected: string;
  readonly document_status_expired: string;
  readonly document_status_not_verified: string;
  // Пятый префикс — долг `TD-68`, решение `D188`. Статус приглашения уезжал в
  // разметку машинным значением, и ключей под него не было ни одного: дыра
  // была **в словаре имён**, объявившем четыре префикса и пропустившем
  // приглашения. Набор ключей сверяется с перечислением контракта тем же
  // сторожем, что и четыре прежних.
  readonly invitation_status_pending: string;
  readonly invitation_status_accepted: string;
  readonly invitation_status_revoked: string;
  readonly invitation_status_expired: string;
  // Шестой по одиннадцатый префиксы — блок `WEB-1.3`, тридцать три ключа
  // шести перечислений контракта. Набор ключей каждого префикса совпадает с
  // порождённым перечнем — за этим следит сторож сирот каркаса; двадцать
  // ключей типов кузова — справочник CR-003 целиком.
  readonly vehicle_kind_tractor: string;
  readonly vehicle_kind_trailer: string;
  readonly body_type_tarpaulin: string;
  readonly body_type_refrigerator: string;
  readonly body_type_isothermal: string;
  readonly body_type_box: string;
  readonly body_type_flatbed: string;
  readonly body_type_platform: string;
  readonly body_type_lowbed: string;
  readonly body_type_mega: string;
  readonly body_type_car_carrier: string;
  readonly body_type_tipper: string;
  readonly body_type_walking_floor: string;
  readonly body_type_tank: string;
  readonly body_type_silo: string;
  readonly body_type_container_chassis: string;
  readonly body_type_bdf: string;
  readonly body_type_timber: string;
  readonly body_type_livestock: string;
  readonly body_type_coil: string;
  readonly body_type_heavy_haul: string;
  readonly body_type_extendable: string;
  readonly repair_state_fault_detected: string;
  readonly repair_state_in_repair: string;
  readonly repair_state_repaired: string;
  readonly repair_state_ready: string;
  readonly vehicle_status_active: string;
  readonly vehicle_status_withdrawn: string;
  readonly vin_claim_status_pending: string;
  readonly vin_claim_status_objected: string;
  readonly vin_claim_status_awaiting_admin: string;
  readonly vin_claim_direction_outgoing: string;
  readonly vin_claim_direction_incoming: string;

  // Ключи блока `WEB-3.2` — словарь §11: двадцать три ключа страницы
  // публичного отслеживания и подписи двух перечней контракта — статуса работы
  // (десять значений) и вида машины перегона (три). Подписи перечней собираются
  // строителями, как и у прежних префиксов.
  readonly tracking_title: string;
  readonly tracking_pin_hint: string;
  readonly tracking_pin_label: string;
  readonly tracking_pin_submit: string;
  readonly tracking_offline: string;
  readonly tracking_offline_refresh: string;
  readonly tracking_status_changed_at: string;
  readonly tracking_vehicle: string;
  readonly tracking_route: string;
  readonly tracking_distance: string;
  readonly tracking_transit: string;
  readonly tracking_border_crossings: string;
  readonly tracking_last_position: string;
  readonly tracking_no_position: string;
  readonly tracking_open_map: string;
  readonly tracking_photos: string;
  readonly tracking_photo_open: string;
  readonly tracking_photo_plate: string;
  readonly tracking_photo_close: string;
  readonly tracking_milestones: string;
  readonly tracking_locked_title: string;
  readonly tracking_retry: string;
  readonly tracking_gone_title: string;
  readonly tracking_map_title: string;
  readonly work_status_CREATED: string;
  readonly work_status_ASSIGNED: string;
  readonly work_status_ACCEPTED: string;
  readonly work_status_AT_PICKUP: string;
  readonly work_status_AT_HANDOVER: string;
  readonly work_status_IN_TRANSIT: string;
  readonly work_status_DELIVERED: string;
  readonly work_status_COMPLETED: string;
  readonly work_status_CANCELLED: string;
  readonly work_status_ARCHIVED: string;
  readonly peregon_vehicle_kind_tractor: string;
  readonly peregon_vehicle_kind_tractor_with_trailer: string;
  readonly peregon_vehicle_kind_car: string;

  // Ключи блока `WEB-2.1` — словарь §11 спецификации блока: экраны логиста
  // («Мои объявления», форма объявления, карточка с offer'ами и доской) и
  // подписи девяти перечней контракта. Подписи перечней собираются
  // строителями ниже; ключ вне перечня префикса не заводится.
  readonly cargo_listings_title: string;
  readonly cargo_listings_create: string;
  readonly cargo_listings_more: string;
  readonly cargo_listings_empty: string;
  readonly cargo_listing_edit: string;
  readonly cargo_listing_close: string;
  readonly cargo_listing_close_confirm: string;
  readonly cargo_listing_km: string;
  readonly cargo_listing_kg: string;
  readonly cargo_listing_days: string;
  readonly cargo_listing_transit: string;
  readonly cargo_listing_borders: string;
  readonly cargo_listing_slots_free: string;
  readonly cargo_listing_slots_of: string;
  readonly cargo_listing_fixed: string;
  readonly cargo_listing_payment: string;
  readonly cargo_listing_form_title_new: string;
  readonly cargo_listing_form_title_edit: string;
  readonly cargo_listing_form_origin: string;
  readonly cargo_listing_form_destination: string;
  readonly cargo_listing_form_city_search: string;
  readonly cargo_listing_form_loading_at: string;
  readonly cargo_listing_form_unload_by: string;
  readonly cargo_listing_form_vehicles_count: string;
  readonly cargo_listing_form_cargo_type: string;
  readonly cargo_listing_form_weight: string;
  readonly cargo_listing_form_adr: string;
  readonly cargo_listing_form_body_type: string;
  readonly cargo_listing_form_body_subtype: string;
  readonly cargo_listing_form_temperature_from: string;
  readonly cargo_listing_form_temperature_to: string;
  readonly cargo_listing_form_price: string;
  readonly cargo_listing_form_currency: string;
  readonly cargo_listing_form_negotiable: string;
  readonly cargo_listing_form_payment_method: string;
  readonly cargo_listing_form_payment_moment: string;
  readonly cargo_listing_form_deferral_days: string;
  readonly cargo_listing_form_comment: string;
  readonly cargo_listing_form_submit: string;
  readonly cargo_listing_form_save: string;
  readonly cargo_offers_title: string;
  readonly cargo_offer_accept: string;
  readonly cargo_offer_decline: string;
  readonly cargo_offer_decline_confirm: string;
  readonly cargo_offer_agrees: string;
  readonly cargo_offer_counter: string;
  readonly cargo_offer_documents_verified: string;
  readonly cargo_offer_vehicles: string;
  readonly cargo_offer_price: string;
  readonly cargo_offer_listing_price: string;
  readonly cargo_offer_expires: string;
  readonly cargo_offer_more: string;
  readonly cargo_slots_title: string;
  readonly cargo_slot_work: string;
  readonly listing_status_OPEN: string;
  readonly listing_status_TAKEN: string;
  readonly listing_status_CLOSED: string;
  readonly listing_status_CANCELLED: string;
  readonly listing_status_PENDING_REVIEW: string;
  readonly listing_status_REJECTED: string;
  readonly cargo_offer_status_PENDING: string;
  readonly cargo_offer_status_APPROVED: string;
  readonly cargo_offer_status_CONFIRMED: string;
  readonly cargo_offer_status_REJECTED: string;
  readonly cargo_offer_status_WITHDRAWN: string;
  readonly cargo_offer_status_EXPIRED: string;
  readonly cargo_offer_status_SUPERSEDED: string;
  readonly cargo_offer_status_CANCELLED: string;
  readonly cargo_slot_status_OPEN: string;
  readonly cargo_slot_status_RESERVED: string;
  readonly cargo_slot_status_TAKEN: string;
  readonly cargo_slot_status_CANCELLED: string;
  readonly currency_USD: string;
  readonly currency_UZS: string;
  readonly currency_EUR: string;
  readonly payment_method_cash: string;
  readonly payment_method_bank_transfer: string;
  readonly payment_moment_prepayment: string;
  readonly payment_moment_on_unloading: string;
  readonly payment_moment_deferred: string;
  readonly loading_type_rear: string;
  readonly loading_type_side: string;
  readonly loading_type_top: string;
  readonly loading_type_full_uncover: string;
  readonly body_parameter_length_m: string;
  readonly body_parameter_width_m: string;
  readonly body_parameter_height_m: string;
  readonly body_parameter_volume_m3: string;
  readonly body_parameter_capacity_t: string;
  readonly body_parameter_loading_type: string;
  readonly body_parameter_temperature: string;
  readonly body_parameter_cars_capacity: string;

  // Ключи блока `WEB-2.2` — словарь §11 спецификации блока: лента грузов со
  // сводкой городов, объявление ленты с формой offer'а, «Мои предложения»,
  // работы фирмы и «ждут назначения», назначение и замена транспорта и
  // водителя, плюс подписи причин недоступности машины (перечень контракта,
  // строитель ниже). Ключ экрана не начинается префиксом перечня.
  readonly cargo_feed_title: string;
  readonly cargo_feed_filters: string;
  readonly cargo_feed_apply: string;
  readonly cargo_feed_reset: string;
  readonly cargo_feed_more: string;
  readonly cargo_feed_details: string;
  readonly cargo_feed_company_verified: string;
  readonly cargo_feed_adr: string;
  readonly cargo_feed_empty: string;
  readonly cargo_feed_loading_from: string;
  readonly cargo_feed_loading_to: string;
  readonly cargo_feed_vehicles_min: string;
  readonly cargo_feed_weight_from: string;
  readonly cargo_feed_weight_to: string;
  readonly cargo_feed_body_type: string;
  readonly cargo_feed_temperature: string;
  readonly cargo_feed_price_from: string;
  readonly cargo_feed_price_to: string;
  readonly cargo_feed_currency: string;
  readonly cargo_feed_q: string;
  readonly cargo_feed_any: string;
  readonly cargo_feed_yes: string;
  readonly cargo_feed_no: string;
  readonly cargo_feed_cities_title: string;
  readonly cargo_feed_cities_total: string;
  readonly cargo_feed_date_hint: string;
  readonly cargo_feed_my_offer: string;
  readonly cargo_feed_listing_no_right: string;
  readonly cargo_feed_listing_unavailable: string;
  readonly cargo_feed_listing_agree: string;
  readonly cargo_feed_listing_own_price: string;
  readonly cargo_feed_listing_fixed_price: string;
  readonly cargo_feed_listing_negotiable: string;
  readonly cargo_feed_listing_submit: string;
  readonly cargo_feed_listing_price_amount: string;
  readonly cargo_offers_mine_title: string;
  readonly cargo_offers_confirm: string;
  readonly cargo_offers_withdraw: string;
  readonly cargo_offers_withdraw_confirm: string;
  readonly cargo_offers_more: string;
  readonly cargo_offers_agrees: string;
  readonly cargo_offers_counter: string;
  readonly cargo_offers_vehicles: string;
  readonly cargo_offers_empty: string;
  readonly cargo_offers_price: string;
  readonly cargo_offers_expires: string;
  readonly cargo_offers_confirm_by: string;
  readonly cargo_offers_sender: string;
  readonly works_to_assign_title: string;
  readonly works_to_assign_empty: string;
  readonly works_internal_new: string;
  readonly internal_work_form_title: string;
  readonly internal_work_form_weight: string;
  readonly internal_work_form_submit: string;
  readonly work_internal: string;
  readonly work_card_internal_cancel: string;
  readonly work_card_internal_cancel_send: string;
  readonly work_card_internal_cancel_question: string;
  readonly works_empty: string;
  readonly works_assign: string;
  readonly works_replace: string;
  readonly work_assignment_title: string;
  readonly work_assignment_tractor: string;
  readonly work_assignment_trailer: string;
  readonly work_assignment_driver: string;
  readonly work_assignment_no_trailer: string;
  readonly work_assignment_driver_busy: string;
  readonly work_assignment_unnamed: string;
  readonly work_assignment_assign: string;
  readonly work_assignment_replace: string;
  readonly work_assignment_current: string;
  readonly work_assignment_assigned_at: string;
  readonly work_assignment_history: string;
  readonly work_assignment_history_empty: string;
  readonly work_assignment_assigned_by: string;
  readonly work_assignment_replaced_by: string;
  readonly work_assignment_unavailable: string;
  readonly unassignable_reason_withdrawn: string;
  readonly unassignable_reason_not_ready: string;
  readonly unassignable_reason_busy: string;

  // Ключи блока `WEB-2.4` — словарь §11 спецификации блока: карточка машины
  // (фото и неисправности), водители фирмы, строки запчастей сервисной записи,
  // файл техпаспорта заявки, дата рождения и пол в профиле — и подписи двух
  // перечней контракта: пола (`gender_`) и типа документа (`document_type_`).
  // Подписи перечней собираются строителями ниже; ключ вне перечня префикса не
  // заводится.
  readonly vehicle_card_title: string;
  readonly vehicle_photo_title: string;
  readonly vehicle_photo_file: string;
  readonly vehicle_photo_add: string;
  readonly vehicle_photo_delete: string;
  readonly vehicle_photo_delete_confirm: string;
  readonly vehicle_photo_empty: string;
  readonly vehicle_fault_title: string;
  readonly vehicle_fault_empty: string;
  readonly vehicle_fault_author_unknown: string;
  readonly vehicle_fault_comment: string;
  readonly vehicle_fault_photos: string;
  readonly vehicle_fault_add: string;
  readonly vehicle_fault_history: string;
  readonly vehicle_fault_open: string;
  readonly vehicle_fault_closed: string;
  readonly vehicle_fault_history_empty: string;
  readonly drivers_title: string;
  readonly drivers_age: string;
  readonly drivers_name_unknown: string;
  readonly drivers_not_specified: string;
  readonly drivers_self_reported: string;
  readonly drivers_documents: string;
  readonly drivers_empty: string;
  readonly service_part_add: string;
  readonly service_part_remove: string;
  readonly service_part_name: string;
  readonly service_part_quantity: string;
  readonly vin_claim_file_label: string;
  readonly vin_claim_file_link: string;
  readonly vin_claim_file_missing: string;
  readonly profile_birth_date: string;
  readonly profile_gender: string;
  readonly profile_gender_unspecified: string;
  readonly profile_personal_note: string;
  readonly profile_personal_hint: string;
  readonly profile_personal_save: string;
  readonly gender_male: string;
  readonly gender_female: string;
  readonly document_type_passport: string;
  readonly document_type_driver_license: string;
  readonly document_type_adr_certificate: string;
  readonly document_type_professional_certificate: string;
  readonly document_type_face_snapshot: string;
  readonly document_type_tachograph_card: string;
  readonly document_type_code95_card: string;
  readonly document_type_visa: string;
  readonly document_type_registration_certificate: string;

  // Ключи блока `WEB-2.3` — словарь §11 спецификации блока: экраны «Расходы» и
  // «Выплаты», их формы и отборы, три исхода подписи получателя, подписи
  // работы и машины строки — и подписи перечня категорий расхода
  // (`expense_category_`), собираемые строителем ниже. Префикс не пересекается
  // с ключами экрана: `expense_form_*` и `expense_receipt_*` начинаются иначе.
  readonly expenses_title: string;
  readonly expenses_create: string;
  readonly expenses_delete_confirm: string;
  readonly expenses_empty: string;
  readonly expense_form_submit: string;
  readonly expense_form_category: string;
  readonly expense_form_spent_on: string;
  readonly expense_form_vehicle: string;
  readonly expense_form_receipt: string;
  readonly expense_receipt_refreshed: string;
  readonly expense_receipt_replace_hint: string;
  readonly payouts_title: string;
  readonly payouts_create: string;
  readonly payouts_delete_confirm: string;
  readonly payouts_empty: string;
  readonly payout_form_submit: string;
  readonly payout_form_recipient: string;
  readonly payout_form_paid_on: string;
  readonly payout_recipient_unnamed: string;
  readonly payout_recipient_not_listed: string;
  readonly payout_recipient_unavailable: string;
  readonly payout_recipient_loading: string;
  readonly accounting_edit: string;
  readonly accounting_delete: string;
  readonly accounting_more: string;
  readonly accounting_works_more: string;
  readonly accounting_filter_reset: string;
  readonly accounting_filter_empty: string;
  readonly accounting_filter_any: string;
  readonly accounting_work_fallback: string;
  readonly accounting_vehicle_fallback: string;
  readonly accounting_vehicle_missing: string;
  readonly accounting_no_vehicle: string;
  readonly accounting_no_work: string;
  readonly accounting_field_amount: string;
  readonly accounting_field_currency: string;
  readonly accounting_field_work: string;
  readonly accounting_field_comment: string;
  readonly accounting_choose: string;
  readonly accounting_form_close: string;
  readonly expense_category_diesel: string;
  readonly expense_category_parking: string;
  readonly expense_category_toll: string;
  readonly expense_category_service: string;
  readonly expense_category_other: string;

  // Ключи блока `WEB-3.1` — карточка работы; перечень по-прежнему закрыт.
  readonly state_retry: string;
  readonly member_unnamed: string;
  readonly work_card_not_available: string;
  readonly work_card_status: string;
  readonly work_card_price: string;
  readonly work_card_vehicle: string;
  readonly work_card_vehicle_model: string;
  readonly work_card_auto_completed: string;
  readonly work_card_confirm: string;
  readonly work_card_confirm_question: string;
  readonly work_card_confirm_by: string;
  readonly work_card_verified: string;
  readonly work_card_not_verified: string;
  readonly work_card_parties: string;
  readonly work_card_customer: string;
  readonly work_card_executor: string;
  readonly work_card_assignment: string;
  readonly work_card_assignment_empty: string;
  readonly work_card_cargo: string;
  readonly work_card_cargo_type: string;
  readonly work_card_cargo_loading: string;
  readonly work_card_cargo_weight: string;
  readonly work_card_cargo_body: string;
  readonly work_card_history: string;
  readonly work_card_history_empty: string;
  readonly work_card_track: string;
  readonly work_card_track_empty: string;
  readonly work_card_track_thinned: string;
  readonly work_card_track_signal: string;
  readonly work_card_track_age: string;
  readonly work_card_track_minutes: string;
  readonly work_card_tiles_failed: string;
  readonly work_card_chat: string;
  readonly work_card_message_text: string;
  readonly work_card_message_photo: string;
  readonly work_card_message_send: string;
  readonly work_card_message_hidden: string;
  readonly work_card_messages_more: string;
  readonly work_card_messages_empty: string;
  readonly work_card_thread: string;
  readonly work_card_thread_caption: string;
  readonly work_card_thread_open: string;
  readonly work_card_files: string;
  readonly work_card_files_empty: string;
  readonly work_card_file_kind: string;
  readonly work_card_file_description: string;
  readonly work_card_file_choose: string;
  readonly work_card_file_uploaded: string;
  readonly work_card_file_uploading: string;
  // Меню каркаса и столбцы таблицы работ — блок `WEB-D.1`, решения `Б1`, `Б4`.
  readonly nav_label: string;
  readonly nav_toggle: string;
  readonly nav_sign_out: string;
  readonly nav_group_works: string;
  readonly nav_group_cargo: string;
  readonly nav_group_fleet: string;
  readonly nav_group_money: string;
  readonly nav_group_company: string;
  readonly works_column_status: string;
  readonly works_column_route: string;
  readonly works_column_cargo: string;
  readonly works_column_loading: string;
  readonly works_column_vehicle: string;
  readonly works_column_driver: string;
  readonly works_column_action: string;
  readonly work_card_file_edit: string;
  readonly work_card_file_save: string;
  readonly work_card_file_delete: string;
  readonly work_card_file_delete_question: string;
  readonly work_card_photos: string;
  readonly work_card_photos_empty: string;
  readonly work_card_photo_plate: string;
  readonly work_card_tracking: string;
  readonly work_card_tracking_create: string;
  readonly work_card_tracking_revoke: string;
  readonly work_card_tracking_revoke_question: string;
  readonly work_card_tracking_revoked: string;
  readonly work_card_tracking_copy: string;
  readonly work_card_tracking_copied: string;
  readonly work_card_tracking_url: string;
  readonly work_card_tracking_pin: string;
  readonly work_card_tracking_once: string;
  readonly work_card_rating: string;
  readonly work_card_rating_score: string;
  readonly work_card_rating_comment: string;
  readonly work_card_rating_send: string;
  readonly work_card_rating_later: string;
  readonly work_card_rating_late: string;
  readonly work_card_claims: string;
  readonly work_card_cancel_request: string;
  readonly work_card_cancel_category: string;
  readonly work_card_cancel_reason: string;
  readonly work_card_cancel_send: string;
  readonly work_card_cancel_respond_by: string;
  readonly work_card_cancel_accept: string;
  readonly work_card_cancel_accept_question: string;
  readonly work_card_cancel_decline: string;
  readonly work_card_cancel_withdraw: string;
  readonly work_card_dispute: string;
  readonly work_card_dispute_open: string;
  readonly work_card_dispute_statement: string;
  readonly work_card_dispute_send: string;
  readonly work_card_dispute_outcome: string;
  readonly work_card_dispute_guilty: string;
  readonly work_card_dispute_note: string;
  readonly work_card_dispute_message: string;
  readonly work_card_dispute_message_send: string;
  readonly work_signal_OK: string;
  readonly work_signal_NO_DATA: string;
  readonly work_signal_LOST: string;
  readonly work_file_kind_document: string;
  readonly work_file_kind_receipt: string;
  readonly work_file_kind_cmr: string;
  readonly work_photo_phase_handover: string;
  readonly work_photo_phase_delivery: string;
  readonly work_photo_phase_loading: string;
  readonly work_photo_phase_unloading: string;
  readonly cancellation_category_VEHICLE_BREAKDOWN: string;
  readonly cancellation_category_ACCIDENT: string;
  readonly cancellation_category_OWNER_DEMAND: string;
  readonly cancellation_category_DOCUMENTS_OR_BORDER: string;
  readonly cancellation_category_EXECUTOR_FORCE_MAJEURE: string;
  readonly cancellation_category_CARGO_WITHDRAWN: string;
  readonly cancellation_category_CARGO_NOT_READY: string;
  readonly cancellation_category_OTHER: string;
  readonly cancellation_request_status_REQUESTED: string;
  readonly cancellation_request_status_ACCEPTED: string;
  readonly cancellation_request_status_REJECTED: string;
  readonly cancellation_request_status_WITHDRAWN: string;
  readonly cancellation_request_status_ESCALATED: string;
  readonly cancellation_request_status_LAPSED: string;
  readonly dispute_status_OPEN: string;
  readonly dispute_status_IN_REVIEW: string;
  readonly dispute_status_ESCALATED: string;
  readonly dispute_status_RESOLVED: string;
  readonly dispute_outcome_CANCEL_WORK: string;
  readonly dispute_outcome_KEEP_WORK: string;
  readonly guilty_side_CUSTOMER: string;
  readonly guilty_side_EXECUTOR: string;
  readonly guilty_side_NONE: string;
  readonly dispute_author_customer: string;
  readonly dispute_author_executor: string;
  readonly dispute_author_support: string;
}

const BUNDLES: Readonly<Record<Locale, Strings>> = { uz, ru, en, tg, tr };

/** Набор строк выбранного языка. */
export function stringsFor(locale: Locale): Strings {
  return BUNDLES[locale];
}

/**
 * Выбранный язык вкладки.
 *
 * Начальное значение — запасное, а не разрешённое: разрешение из браузера
 * делает оболочка при монтировании и кладёт его сюда. Разрешать язык при
 * загрузке модуля значило бы читать браузер там, где его читать некому.
 */
let selected: Locale = FALLBACK_LOCALE;

/**
 * Единственный способ сменить язык — и он один намеренно.
 *
 * Гость меняет язык им и только им; у вошедшего решение принимает сервер, и
 * сюда кладётся язык **из ответа** `PATCH /v1/me`, а не выбранное значение.
 * Второе имя смены завело бы второе место, где живёт язык.
 */
export function setLocale(locale: Locale): Strings {
  selected = locale;

  return BUNDLES[selected];
}

/** Язык, выбранный сейчас. */
export function activeLocale(): Locale {
  return selected;
}

/**
 * Имя ключа подписи по значению перечня — единственное место сборки.
 *
 * Точка в значении становится подчёркиванием: значения права содержат точки, а
 * имя ключа их не содержит. Правило превращения выбрано один раз и записано
 * словарём имён блока — второй выбор разошёлся бы с наборами языков молча, и
 * причину искали бы в переводах.
 *
 * Приведение к ключу набора здесь неизбежно и названо: значение приходит с
 * сервера, а перечень значений сервера шире перечня, известного клиенту
 * (CR-007). Значение вне перечня даёт имя, которого в наборе нет, и подпись
 * ему выбирает `enumLabel` — «неизвестно» без исходной строки.
 */
function enumStringKey(prefix: string, value: string): keyof Strings {
  return `${prefix}${value.split('.').join('_')}` as keyof Strings;
}

/** Ключ подписи роли членства. */
export function roleStringKey(role: string): keyof Strings {
  return enumStringKey('role_', role);
}

/** Ключ подписи статуса членства. */
export function memberStatusStringKey(status: string): keyof Strings {
  return enumStringKey('member_status_', status);
}

/** Ключ подписи права из закрытого каталога. */
export function permissionStringKey(right: string): keyof Strings {
  return enumStringKey('permission_', right);
}

/** Ключ подписи статуса документа. */
export function documentStatusStringKey(status: string): keyof Strings {
  return enumStringKey('document_status_', status);
}

/** Ключ подписи статуса приглашения. */
export function invitationStatusStringKey(status: string): keyof Strings {
  return enumStringKey('invitation_status_', status);
}

// Шестой по одиннадцатый строители — блок `WEB-1.3`: столько перечислений
// блок показывает человеку, и число выведено из контракта, а не выбрано.
// Рукописная таблица «значение → ключ» здесь запрещена — подписи собираются
// только строителем с объявленным префиксом.

/** Ключ подписи вида транспорта. */
export function vehicleKindStringKey(kind: string): keyof Strings {
  return enumStringKey('vehicle_kind_', kind);
}

/** Ключ подписи типа кузова — двадцать позиций справочника CR-003. */
export function bodyTypeStringKey(type: string): keyof Strings {
  return enumStringKey('body_type_', type);
}

/** Ключ подписи состояния цикла ремонта. */
export function repairStateStringKey(state: string): keyof Strings {
  return enumStringKey('repair_state_', state);
}

/** Ключ подписи статуса записи реестра транспорта. */
export function vehicleStatusStringKey(status: string): keyof Strings {
  return enumStringKey('vehicle_status_', status);
}

/** Ключ подписи статуса заявки на освобождение VIN. */
export function vinClaimStatusStringKey(status: string): keyof Strings {
  return enumStringKey('vin_claim_status_', status);
}

/** Ключ подписи направления заявки — чья она для читающей фирмы. */
export function vinClaimDirectionStringKey(direction: string): keyof Strings {
  return enumStringKey('vin_claim_direction_', direction);
}

// Двенадцатый и тринадцатый строители — блок `WEB-3.2`: статус работы и вид
// машины перегона на странице публичного отслеживания. Префикс вида машины
// свой, а не `vehicle_kind_`: перечни разные, и у перегона прицепа отдельно нет.

/** Ключ подписи статуса работы — и текущего, и вехи. */
export function workStatusStringKey(status: string): keyof Strings {
  return enumStringKey('work_status_', status);
}

/** Ключ подписи вида машины перегона. */
export function peregonVehicleKindStringKey(kind: string): keyof Strings {
  return enumStringKey('peregon_vehicle_kind_', kind);
}

// Строители блока `WEB-2.1` — по одному на каждый перечень кабинета логиста.
// Подпись типа фирмы собирается здесь же, и рукописной таблицы «значение →
// ключ» у неё больше нет (долг `TD-73`).

/** Ключ подписи типа фирмы. */
export function companyTypeStringKey(kind: string): keyof Strings {
  return enumStringKey('company_type_', kind);
}

/** Ключ подписи статуса объявления — общий перечень груза и перегона. */
export function listingStatusStringKey(status: string): keyof Strings {
  return enumStringKey('listing_status_', status);
}

/** Ключ подписи статуса offer'а на груз. */
export function cargoOfferStatusStringKey(status: string): keyof Strings {
  return enumStringKey('cargo_offer_status_', status);
}

/** Ключ подписи статуса слота доски заказа. */
export function cargoSlotStatusStringKey(status: string): keyof Strings {
  return enumStringKey('cargo_slot_status_', status);
}

/** Ключ подписи валюты. */
export function currencyStringKey(currency: string): keyof Strings {
  return enumStringKey('currency_', currency);
}

/** Ключ подписи способа оплаты. */
export function paymentMethodStringKey(method: string): keyof Strings {
  return enumStringKey('payment_method_', method);
}

/** Ключ подписи момента оплаты. */
export function paymentMomentStringKey(moment: string): keyof Strings {
  return enumStringKey('payment_moment_', moment);
}

/** Ключ подписи типа загрузки кузова. */
export function loadingTypeStringKey(kind: string): keyof Strings {
  return enumStringKey('loading_type_', kind);
}

/** Ключ подписи параметра кузова — имени, которым справочник называет применимое. */
export function bodyParameterStringKey(parameter: string): keyof Strings {
  return enumStringKey('body_parameter_', parameter);
}

/** Ключ подписи причины, по которой машину нельзя назначить, — блок `WEB-2.2`. */
export function unassignableReasonStringKey(reason: string): keyof Strings {
  return enumStringKey('unassignable_reason_', reason);
}

// Строители блока `WEB-2.4`: пол человека — на экране «Водители фирмы» и в
// профиле; тип документа — на экране документов члена фирмы. Перечень типов
// документа порождался и раньше, а подписи у него не было: тип не показывался
// вовсе, и механик не отличил бы права от ADR. Префикс `document_type_` с
// `document_status_` не пересекается — перечни разные.

/** Ключ подписи пола человека. */
export function genderStringKey(gender: string): keyof Strings {
  return enumStringKey('gender_', gender);
}

/** Ключ подписи типа документа. */
export function documentTypeStringKey(type: string): keyof Strings {
  return enumStringKey('document_type_', type);
}

// Строитель блока `WEB-2.3`: категория расхода — на экране «Расходы» строкой и
// в выборе формы. Перечень категорий закрыт контрактом; значение вне его —
// «неизвестно» без исходной строки (CR-007).

/** Ключ подписи категории расхода. */
export function expenseCategoryStringKey(category: string): keyof Strings {
  return enumStringKey('expense_category_', category);
}

// Строители блока `WEB-3.1` — подписи карточки работы.

/** Ключ подписи признака связи работы. */
export function workSignalStringKey(value: string): keyof Strings {
  return enumStringKey('work_signal_', value);
}

/** Ключ подписи вида файла работы. */
export function workFileKindStringKey(value: string): keyof Strings {
  return enumStringKey('work_file_kind_', value);
}

/** Ключ подписи фазы кадра работы. */
export function workPhotoPhaseStringKey(value: string): keyof Strings {
  return enumStringKey('work_photo_phase_', value);
}

/** Ключ подписи категории отмены. */
export function cancellationCategoryStringKey(value: string): keyof Strings {
  return enumStringKey('cancellation_category_', value);
}

/** Ключ подписи статуса запроса отмены. */
export function cancellationRequestStatusStringKey(value: string): keyof Strings {
  return enumStringKey('cancellation_request_status_', value);
}

/** Ключ подписи статуса спора. */
export function disputeStatusStringKey(value: string): keyof Strings {
  return enumStringKey('dispute_status_', value);
}

/** Ключ подписи итога спора. */
export function disputeOutcomeStringKey(value: string): keyof Strings {
  return enumStringKey('dispute_outcome_', value);
}

/** Ключ подписи виновной стороны. */
export function guiltySideStringKey(value: string): keyof Strings {
  return enumStringKey('guilty_side_', value);
}

/** Ключ подписи автора сообщения спора. */
export function disputeAuthorStringKey(value: string): keyof Strings {
  return enumStringKey('dispute_author_', value);
}

/**
 * Подпись значения перечня, неизвестного клиенту, — **без исходной строки**
 * (CR-007, вариант A).
 *
 * Исходная строка в подписи означала бы, что человек читает машинное значение
 * сервера и делает из него вывод; вывод этот делать нечем.
 */
export function unknownEnumLabel(strings: Strings): string {
  return strings.enum_unknown;
}

/**
 * Язык интерфейса по перечню языков браузера.
 *
 * Берётся первый поддерживаемый **по порядку браузера**. Взять «`ru`, потому
 * что он раньше в нашем перечне» означает показать русский тому, кто попросил
 * таджикский первым.
 */
export function resolveLocale(preferred: readonly string[]): Locale {
  for (const tag of preferred) {
    const primary = tag.trim().toLowerCase().split('-')[0];

    if (primary === undefined || primary === '') {
      continue;
    }

    for (const locale of SUPPORTED_LOCALES) {
      if (locale === primary) {
        return locale;
      }
    }
  }

  return FALLBACK_LOCALE;
}
